import { Label, Pie, PieChart } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  ratioFormatter,
} from "@/components/ui/chart";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import {
  ExploreChartEmptyState,
  ExploreChartLoader,
} from "./explore-widgets-utils";
import { useExploreChart } from "./useExploreChart";
import { Flex } from "@radix-ui/themes";

interface ExplorePieChartProps {
  source?: ExploreInteractionType;
  groupBy?: string;
  filter?: Filter;
}

export function ExplorePieChart({
  source,
  groupBy,
  filter,
}: ExplorePieChartProps) {
  const { items, isLoading, chartConfig, totalVolume } = useExploreChart(
    source,
    groupBy,
    filter
  );

  if (isLoading) {
    return <ExploreChartLoader />;
  }

  if (items.length === 0) {
    return <ExploreChartEmptyState variant="no-data" />;
  }

  return (
    <Flex width={"100%"} height={"100%"} justify={"center"} align={"center"}>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square h-full"
      >
        <PieChart margin={{ top: 16, right: 16, bottom: 16, left: 16 }}>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent indicator="line" />}
          />
          <Pie
            data={items}
            dataKey={"volume"}
            nameKey={"value"}
            innerRadius={60}
            strokeWidth={5}
            labelLine={false}
            label={({ payload, ...props }) => {
              return (
                <text
                  cx={props.cx}
                  cy={props.cy}
                  x={props.x}
                  y={props.y}
                  textAnchor={props.textAnchor}
                  dominantBaseline={props.dominantBaseline}
                  fill="hsla(var(--foreground))"
                >
                  {ratioFormatter(payload.volume / totalVolume)}
                </text>
              );
            }}
          >
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground text-3xl font-bold"
                      >
                        {totalVolume.toLocaleString()}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 24}
                        className="fill-muted-foreground"
                      >
                        Volume
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
          <ChartLegend content={<ChartLegendContent />} />
        </PieChart>
      </ChartContainer>
    </Flex>
  );
}
